
import { Component, Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import Groups from '../../group/Groups';
import GroupResultsModal from './GroupResultsModal';
import { emptyUUID, WindowType } from '../../../util';
import {
  ApprovalState,
  GroupSurveyExtension,
  GroupSurveyExtensionUiClient,
  IGroupSurveyExtension,
  SettingsApiControllerUIClient,
} from '../../backend/backendapi';
import { exposeHeaders, initClient } from '../../../api';
import Toasted from 'vue-toasted';
import GroupSurveyExtensionStore from '../../../stores/GroupSurveyExtensionStore';
import GroupsForSurveyEditModal from './GroupsForSurveyEditModal.vue';
import { Results, VgtParameters, VgtRemoteManger } from '../../../util/VgtRemoteManger';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  methods: { emptyUUID },
  components: {
    GroupsForSurveyEditModal,
    GroupResultsModal,
    Groups,
  },
})
export default class GroupsForSurvey extends Vue {
  @Prop({ default: false })
  readonly advanceRegistration!: boolean;
  @Prop({ default: false })
  readonly showPinnedGroupsOnly!: boolean;
  @Prop({ default: false })
  readonly allowNoEmailForExternalUsers!: boolean;
  @Prop({ default: false })
  readonly remote!: boolean;

  private readonly api = exposeHeaders(initClient(GroupSurveyExtensionUiClient), 'list');
  private readonly settingsApi = initClient(SettingsApiControllerUIClient);
  readonly store = new GroupSurveyExtensionStore();

  GroupSurveyExtension = GroupSurveyExtension;

  extraInfoJson: string = '[]';
  vgtManager: VgtRemoteManger<GroupSurveyExtension> | null = null;

  get canSurveyAdmin(): boolean {
    return window.application.permissions.survey.admin;
  }

  get groups(): GroupSurveyExtension[] {
    if (this.remote) {
      return [];
    }
    return this.store.groupSurveyExtensions.filter(e => !e.group?.archived && e.isAdvanceRegistration === this.advanceRegistration);
  }

  async mounted(): Promise<void> {
    await this.store.reload();
    await this.reloadSettings();
  }

  initGroup(): GroupSurveyExtension {
    return GroupSurveyExtension.fromJS({
      id: emptyUUID(),
      groupId: emptyUUID(),
      group: (this.$refs.groups as Groups).groupInitFunctionInternal(),
      isAdvanceRegistration: this.advanceRegistration,
      surveyMappings: [],
      enableScanEntry: false,
      scanEntryEndDate: undefined,
      scanEntryStartDate: undefined,
      responsibleContactId: undefined,
      responsibleFallbackContactId: undefined,
      collectiveApprovalState: ApprovalState.NotRequired,
      isVisitorGroup: false,
      extraInfoJson: this.extraInfoJson,
      visitorGroupNames: [],
      userPresences: [],
      approvals: [],
      informationGroupMappings: [],
    } as IGroupSurveyExtension);
  }

  private async loadGroups(params: VgtParameters): Promise<Results<GroupSurveyExtension>> {
    const response = await this.api.list(params.filter, params.sort, params.page, params.pageSize);
    return { data: response.content, totalRows: parseInt(response.headers.get('total') ?? '0') };
  }

  async saveGroup(group: GroupSurveyExtension): Promise<GroupSurveyExtension> {
    let res: GroupSurveyExtension;
    if (!group.id || group.id === emptyUUID()) {
      res = await this.api.create(group);
    } else {
      res = await this.api.update(group);
    }
    this.store.rememberGroupSurveyExtension(res);
    return res;
  }

  async deleteGroup(group: GroupSurveyExtension): Promise<void> {
    await this.api.delete(group.id);
    this.store.forgetGroupSurveyExtension(group);
  }

  async reloadSettings(): Promise<void> {
    try {
      const extraInfoJson = await this.settingsApi.getSystemSetting('VisitorManagementSettings.AdvanceRegistrationExtraInfoJson');
      if (Array.isArray(extraInfoJson.result!)) {
        this.extraInfoJson = JSON.stringify(extraInfoJson.result);
      } else {
        this.extraInfoJson = '[]';
      }
    } catch (error) { //in case the setting does not exist
      console.error(error);
      this.extraInfoJson = '[]';
    }
  }

  onResultCombined(group: GroupSurveyExtension): void {
    window.location.href = this.url(group.id) + '/resultscombined';
  }

  private updateSearchParams(): void {
    if (!this.vgtManager) {
      return;
    }

    let newExtraFilter = '';
    newExtraFilter = [newExtraFilter, 'isAdvanceRegistration = ' + this.advanceRegistration]
        .filter(e => e.trim().length > 0)
        .join(' AND ');
    if (this.showPinnedGroupsOnly) {
      newExtraFilter = [newExtraFilter, 'group.isPinned = ' + this.showPinnedGroupsOnly]
          .filter(e => e.trim().length > 0)
          .join(' AND ');
    }
    if (newExtraFilter !== this.vgtManager.extraFilter) {
      this.vgtManager.extraFilter = newExtraFilter;
    }
  }

  url(id: string): string {
    return window.location.protocol + '//'
        + window.location.hostname
        + (window.location.port?.length ? ':' + window.location.port : '')
        + window.application.baseUrl
        + 'survey/groups/' + id;
  }

  vgtColumnModifier(columns: unknown[]): unknown[] {
    columns.splice(-1, 0, {
      label: this.$t('txtContactLastName').toString(),
      field: 'firstExternalUserFirstNameLetterAndLastName',
    });
    return columns;
  }

  @Watch('remote', { immediate: true })
  onRemoteChanged(): void {
    if (!this.remote) {
      this.vgtManager = null;
      return;
    }
    this.vgtManager = new VgtRemoteManger<GroupSurveyExtension>(this.loadGroups.bind(this), 'en', [
      { field: 'group.name', type: 'string' },
      { field: 'group.createdOn', type: 'date' },
      { field: 'group.createdByFullName', type: 'string' },
      { field: 'group.isPinned', type: 'boolean' },
      { field: 'firstExternalUserFirstNameLetterAndLastName', type: 'string' },
      { field: 'isAdvanceRegistration', type: 'boolean' },
    ]);
    this.updateSearchParams();
  }

  @Watch('showPinnedGroupsOnly', { immediate: true })
  @Watch('advanceRegistration', { immediate: true })
  onRemoteParamsChanged(): void {
    this.updateSearchParams();
  }
}
